<!-- eslint-disable -->
<template>
  <Dropdown
    v-model="advancedSearch.status"
    :options="statusOptions"
    optionLabel="label"
    optionValue="value"
    class="status-dropdown"
    :dropdown="true"
    :disabled="quoteIsWonOrRejected || quoteIsDeleted"
    @change="statusChangeCheckForJobCreate"
  >
  </Dropdown>
</template>

<script lang="ts" setup>
/* eslint-disable */
import {
  ref,
  toRefs,
  computed,
  watch,
  toRaw,
  ComputedRef,
  PropType
} from "vue";
import {
  statuses,
  calculateTotal,
  SKQuoteFields,
  SKQuoteLineItemFields,
  isEmpty,
  isJobTypeWellTestRental,
  SgqJobTypes,
  SgqQuoteTypes
} from "@/utils/quoteHelpers";
import { useRoute, useRouter } from "vue-router";
import { decimalFormatter } from "@/utils/utils";
import {
  AllQuoteFieldsFragment as QuoteType,
  Quote_Line_Items,
  useInsertJobMutation,
  useInsertSkQuoteMutation,
  useUpdateQuoteByPkMutation,
  useUpdateSkQuoteJobMutation,
  SkJobInput,
} from "gql/api"
import { captureException } from "@sentry/vue";
import { DropdownChangeEvent } from "primevue/dropdown";
import { useToast } from "primevue/usetoast";
import { GraphQLError } from "graphql";

// MUTATIONS:
const { executeMutation: insertJob } = useInsertJobMutation();
const { executeMutation: insertSkQuote } = useInsertSkQuoteMutation();
const { executeMutation: updateQuote } =
  useUpdateQuoteByPkMutation();
const { executeMutation: updateSKQuoteJob } = useUpdateSkQuoteJobMutation();

// PROPS:
const props = defineProps({
  quote: {
    type: Object as PropType<QuoteType | null>,
    default: null
  },
  advancedSearch: {
    type: Object,
    default: {}
  },
  saveQuote: {
    type: Function,
    required: true,
  }
})

// TOREFS:
const { quote, advancedSearch  } = toRefs(props)

// REACTIVES - REFS:
const route = useRoute();
const router = useRouter();

// REACTIVES - COMPUTEDS:
const statusOptions = ref<Array<any>>([
  { value: statuses.draft, label: "Draft" }
]);
const quoteIsWonOrRejected: ComputedRef<boolean> = computed(() => {
  return [statuses.won, statuses.rejected].some(
    status => status === quote?.value?.status
  );
});
const quoteIsDeleted: ComputedRef<boolean> = computed(() => {
  return Boolean(quote?.value?.deleted_at);
});

// NONREACTIVES:
if (router.currentRoute.value.fullPath.includes("/quote/new")) {
  advancedSearch.value.status = statuses.draft;
}

/**
 * @param {string} name - the name provided from user.display_name
 * @returns {string} The initials from first and last name (example: LC)
 */
function getInitials(name: string) {
  return name
    .trim()
    .split(/\s+/)
    .map(word => word[0].toUpperCase())
    .join("");
}

/**
 * @param {object} obj - the source object to pick properties from
 * @param {any[]} keys - an array of string keys to pick the object  from
 * @returns {object} - a new object containing properties from a specified object
 */
function pick<T extends object, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> {
  const ret: Partial<T> = {};
  for (const key of keys) {
    ret[key] = obj[key];
  }
  return ret as Pick<T, K>; // Return the new object with the picked properties
}

const winCreateLabel = "Won (Create New Job)"
const winUpdateLabel = "Won (Update Existing Job)"
const toast = useToast();

/**
 * This function triggers actions based on the status selected from the dropdown.
 *
 * @param {DropdownChangeEvent} evt - The dropdown event triggered from the Dropdown
 * @returns {void} - returns nothing, just a long block of status-related actions
 */
const statusChangeCheckForJobCreate = (evt: DropdownChangeEvent): void => {
  const label = (evt?.originalEvent?.currentTarget as HTMLElement)?.innerHTML;
  const selectedStatus = advancedSearch.value.status;
  const previousStatus = quote?.value?.status;

  // if sk_job_id exists, allow Update or Create (second time Won)
  // if sk_job_id does not exist, must Create    (first time Won)
  if (
    label === winCreateLabel &&
    selectedStatus === statuses.won
  ) {
    if (isEmpty(quote?.value?.sk_job_type)) {
      toast.add({
        severity: "error",
        summary: "Missing header data",
        detail: "A 'Job Type' and 'Quote Type' must be selected and saved.",
        life: 7000
      });
      advancedSearch.value.status = previousStatus;
      return;
    }

    if (!quote.value?.user?.display_name?.trim() || !quote.value?.user?.hal_identifier) {
      toast.add({
        severity: "error",
        summary: "Prepared By Required",
        detail: "Select and save a Prepared By user with a name and Halliburton ID before creating a job.",
        life: 7000
      });
      advancedSearch.value.status = previousStatus;
      return;
    }

    // allow create job even when sk job id exists
    Promise.all([
      props.saveQuote()
    ]).then(_resp => {
      if (!quote.value) return Promise.reject(new Error("[saveQuote] Quote value is falsey"));
      // Get the latest quote data after saving to prevent race condition
      const q = quote.value;

      const total = (q?.quote_line_items as Quote_Line_Items[]).reduce(
          (acc: number, item: Quote_Line_Items) => acc + Number(calculateTotal(item)), 0);

      const quote_total = Number(decimalFormatter(total));

      const jobData: SkJobInput = {
        author: q?.user?.hal_identifier,
        number: `${getInitials(q?.user?.display_name as string)}-QUOTE-${q.sk_proposal_number}`,
        date: q.proposed_start_date,
        salesman: q?.user?.hal_identifier,
        ship_to: String(q?.erp_address_id),
        sold_to: String(q?.erp_customer_number),
        credit_check_sent: false,
        pricing_date: q?.pricing_date,
        account_manager: q?.user?.hal_identifier,
        currency: q?.currency,
        price_list_number: String(q?.erp_price_list_number),
        // SK resolves the NWA by id; the name is for display only.
        natural_work_area: q?.erp_nwa_name,
        nwa_id: q?.erp_nwa_id ? String(q.erp_nwa_id) : null,
        // quote_number: q?.name,
        source: "6",
        plant_name: q?.erp_plant_name,
        plant_erp_id: String(q?.erp_plant_id),
        prepared_by: null,
        proposal_number: q?.sk_proposal_number,
        proposal_version: String(q?.version_number),
        sales_office: String(q?.erp_plant_id),
        // TODO: revisit how type scoping is handled, potential room for general logic improvements
        job_type: isJobTypeWellTestRental(q?.sk_job_type as string)
          ? SgqJobTypes.RENTAL
          : SgqJobTypes.SALE,
        // Dev note: job_bom is automatically assigned in SK, no need to pass here
        proposed_job_start_date: q?.proposed_start_date,
        // quote_total,
        // TODO: quote total
        spyglass_quote_id: q?.id
      };

      if (q?.erp_contract_number) {
        jobData.contract = String(q.erp_contract_number);
      }

      if (q.erp_well_id) {
        jobData.erp_well = String(q.erp_well_id);
      } else {
        jobData.ship_to = String(q?.erp_address_id);
      }

      toast.add({
        severity: "info",
        summary: "Creating Job Number",
        detail: `Creating job number, please wait:\n${jobData?.number}`,
        life: 7000
      })

      if (q?.sk_planning_order) {
        // If not omitted and blank is sent, SK will return server
        // error 500 and reports 'erp_well_id' does not exist
        jobData.planning_order = q.sk_planning_order
      }

      return insertJob({
        object: jobData
      }).then(_resp => {
        if (!quote.value) return Promise.reject(new Error("[insertJob] Quote value is falsey"));
        const job = _resp?.data?.createSKJob;
        let quotePromise = Promise.resolve();
        if (job && job?.number) {
          toast.add({
            severity: "success",
            summary: "Job Number Created",
            detail: `${job?.number}`,
            life: 7000
          });
          const sk_job_number = job?.number;
          const sk_job_id = job?.id;

          advancedSearch.value.sk_job_id = sk_job_id;
          quote.value.sk_job_id = sk_job_id;

          advancedSearch.value.sk_job_number = sk_job_number;
          quote.value.sk_job_number = sk_job_number;

          const skQuote = {
            job: sk_job_id,
            spyglass_id: quote?.value?.id,
            rules: (quote?.value?.quote_line_items as Quote_Line_Items[]).map((qli) => {
              return pick(toRaw(qli), SKQuoteLineItemFields);
            })
          };

          Object.assign(
            skQuote,
            pick(toRaw(quote.value), SKQuoteFields as (keyof QuoteType)[])
          );

          quotePromise = Promise.resolve()
            .then(() => {
              return insertSkQuote({
                object: skQuote
              })
            })
            .then(__resp => {
              if (!quote.value) return Promise.reject(new Error("[insertSkQuote] Quote value is falsey"));

              if (!__resp?.data?.createSKQuote) {
                captureException(new Error("QUOTE: sk quote create failed"), {
                  extra: {
                    quote_id: quote.value.id,
                    response: __resp
                  }
                });
                console.error("Error creating sk quote");
                quote.value.error_log_json = JSON.stringify({
                  errors: [
                    {
                      message: "Error creating quote",
                      code: "SK_QUOTE_CREATION_ERROR",
                      response: _resp?.data
                    }
                  ]
                });
              }
            });
        } else {
          // If the GraphQL query responds with 200, but Django responds with 400/500
          if (!job?.number) {
            const gqlResponse: GraphQLError | undefined = _resp?.error?.graphQLErrors?.[0];
            // Show SK's reason (e.g. a well without a ship-to address, SK-5191).
            const skReason = gqlResponse?.message ? `\n\n${gqlResponse.message}` : "";
            toast.add({
              severity: "error",
              summary: "Missing Job Number",
              detail: `SK did not create the Job Number.${skReason}\n\nTry again or contact support.`,
              life: 15000
            });

            const gqlCode: string = typeof gqlResponse?.extensions?.code === 'string' ? gqlResponse?.extensions?.code : ''
            if (gqlCode.includes("SAP_SERVER_NAME_MISMATCH_ERROR")) {
              const jobTypeNumber = quote?.value?.sk_job_type?.split('_')?.[1] || 'unknown';
              if (window.config.ENV !== "production") {
                toast.add({
                  severity: "warn",
                  summary: `Dev env detected - Job Type ${jobTypeNumber} is not available in SK`,
                  detail: `
                    Update the Job Type and try again.\n
                    See the network response OR console error for more information!`,
                  life: 20000
                });

                const gqlMessage = String(gqlResponse?.message)
                    .trim()
                    .replace(/\s+/g, ' ')
                const gqlError = String(gqlResponse?.extensions?.error)
                    .trim()
                    .replace(/\s+/g, ' ')
                const gqlUrls = String(gqlResponse?.extensions?.urls).split(',')
                    .map(url => url.trim())
                    .join('\n')

                console.error("Error creating job: " + gqlMessage + "\n\n" + gqlError + "\n\n" + gqlUrls)
              }
            }
          }
          captureException(new Error("QUOTE: job create failed"), {
            extra: {
              quote_id: quote.value.id,
              response: _resp
            }
          });
          if (props?.quote) {
            props.quote.error_log_json = JSON.stringify({
              errors: [
                {
                  message: "Error creating job",
                  code: "JOB_CREATION_ERROR",
                  response: _resp?.data
                }
              ]
            });
          } else {
            console.error("[insertJob] props.quote is falsey");
          }
          console.error("Error creating job: ", _resp?.data);
          advancedSearch.value.status = statuses.published;
        }

        // saveQuote
        return Promise.all([props.saveQuote(), quotePromise]);
      });
    });
  } else if (
    label === winUpdateLabel &&
    selectedStatus === statuses.won &&
    quote?.value?.sk_job_id
  ) {
    // update job

    const skQuote = Object.assign({
      job: quote?.value.sk_job_id,
      spyglass_id: quote?.value.id,
      job_type: isJobTypeWellTestRental(quote?.value?.sk_job_type as string)
        ? SgqJobTypes.RENTAL
        : SgqJobTypes.SALE,
      rules: (quote?.value?.quote_line_items as Quote_Line_Items[]).map((qli: Quote_Line_Items) => {
        return pick(toRaw(qli), SKQuoteLineItemFields);
      })
    },
      pick(toRaw(quote?.value), SKQuoteFields as (keyof QuoteType)[])
    )

    const updateQuoteJobPromise = Promise.all([
        props.saveQuote()
      ]).then(() => {
        return updateSKQuoteJob({
          object: skQuote
        });
      })
      .then((del_resp) => {
        if (!del_resp) {
          console.error("Error updating sk quote job.");
        }
      });
  }
};

// WATCHERS:
watch(quote, (newQuote, oldQuote) => {
  // TODO: we have a similar watcher in EditQuote.vue, possible candidate
  // for putting this there?
  const capitalizeLabels = () => {
    statusOptions.value.forEach((val) =>
      val.label = val.label.charAt(0).toUpperCase() + val.label.slice(1)
    )
  }

  if (quote.value !== null) {
    advancedSearch.value.status = newQuote?.status || statuses.draft;
    switch (newQuote?.status) {
      case statuses.draft:
        statusOptions.value = [
          { value: statuses.published, label: statuses.published },
          { value: statuses.draft, label: statuses.draft }
        ];
        capitalizeLabels();
        break;
      case statuses.rejected:
        statusOptions.value = [
          { value: statuses.rejected, label: statuses.rejected }
        ];
        capitalizeLabels();
        break;
      case statuses.won:
        statusOptions.value = [
          { value: statuses.won, label: statuses.won }
        ];
        capitalizeLabels();
        break;
      case statuses.published:
        if (!quote?.value.sk_job_id) {
          statusOptions.value = [
            { value: statuses.won, label: winCreateLabel },
            { value: statuses.rejected, label: statuses.rejected },
            { value: statuses.published, label: statuses.published }
          ];
        } else {
          statusOptions.value = [
            { value: statuses.won, label: winCreateLabel },
            { value: statuses.won, label: winUpdateLabel },
            { value: statuses.rejected, label: statuses.rejected },
            { value: statuses.published, label: statuses.published }
          ];
        }
        capitalizeLabels();
        break;
      default:
        statusOptions.value = [
          { value: statuses.published, label: statuses.published },
          { value: statuses.draft, label: statuses.draft }
        ];
        capitalizeLabels();
        break;
    }
  }}
)
</script>

<style lang="scss" scoped>
.status-dropdown {
  /* Just guestimating here, feel free to change this */
  width: 50%;
  @media only screen and (max-width: 1198px) {
    width: 65%;
  }
}
</style>
